import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import i18n from 'i18next'
import {
  DepartmentSettingsProps,
  MedicalInstitutionProps,
  reservationDeadlineTimings,
  SelectOptionsProps,
  SettlementsNames,
} from 'types/med'
import RegularReservationFrameSetting from 'ui/organisms/ReservationFrameSetting/RegularReservationFrameSetting'
import { QuestionTooltipWithLabel } from 'ui/shorthands/QuestionTooltip'
import IndividualReservationSettingList from 'ui/organisms/ReservationFrameSetting/IndividualReservationSettingList'
import CloseDateSettingList from 'ui/organisms/CloseDateSettingList'
import { Text1, H2, H3 } from 'ui/shorthands/Texts'
import { StackItem } from 'ui/layout/StackItem'
import LinkUrlText from 'ui/utils/LinkUrlText'

import {
  DepartmentStaticItems,
  reservablePeriods,
  useStylesDepartment,
} from './DepartmentEdit'
import { CallMethodRead } from './CallMethod'

export type DepartmentReadProps = {
  med: MedicalInstitutionProps
  settings: DepartmentSettingsProps
}

const SelectOptionValue = (value: SelectOptionsProps) => {
  switch (value) {
    case 'required':
      return i18n.t('必須')
    case 'optional':
      return i18n.t('任意')
    case 'not':
      return i18n.t('使用しない')
  }
}

export const callSettingHeaderContent = (
  <QuestionTooltipWithLabel label={i18n.t('通話')}>
    <Trans
      i18nKey={
        '電話とビデオ通話を両方有効にすると、申込者が予約時に選択することができます。<br /> 電話とビデオ通話を両方無効にすると、通話なし科目になります。'
      }
    />
  </QuestionTooltipWithLabel>
)

const DepartmentRead: React.FC<DepartmentReadProps> = ({
  settings,
}: DepartmentReadProps) => {
  const { t } = useTranslation()
  const { classes } = useStylesDepartment()
  return (
    <div className={classes.content}>
      <H2>{t('受付科目')}</H2>
      <div className={classes.category}>
        <StackItem label={t('科目名')}>
          <Text1>{settings.name}</Text1>
        </StackItem>
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('科目説明文')}>
              {t('申込者が予約する画面に表示されます。')}
            </QuestionTooltipWithLabel>
          }
          alignTop
        >
          <Text1>
            <LinkUrlText>{settings.supplementary_text}</LinkUrlText>
          </Text1>
        </StackItem>
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('ステータス')}>
              {t('公開にすると予約フォームに表示されます。')}
            </QuestionTooltipWithLabel>
          }
        >
          <Text1>{settings.is_open ? t('公開') : t('非公開')}</Text1>
        </StackItem>
        <StackItem
          label={
            <QuestionTooltipWithLabel
              label={t('予約完了メールに記載する連絡事項')}
            >
              {t(
                '予約完了メールに連絡事項として表示されます。Web問診のリンク、診療当日準備するものなどを予約者に伝えることができます。'
              )}
            </QuestionTooltipWithLabel>
          }
          alignTop
        >
          {settings?.information_for_reservation && (
            <Text1>
              <LinkUrlText>{settings.information_for_reservation}</LinkUrlText>
            </Text1>
          )}
        </StackItem>
      </div>
      <H2 className={classes.parentHeader}>{t('予約カレンダー')}</H2>
      <H3 className={classes.subHeader}>{t('設定')}</H3>
      <div className={classes.category}>
        <StackItem label={t('予約受付期間')}>
          <Text1>
            {t('当日から')}
            {
              reservablePeriods.find(
                (p) => p.value === settings.reservable_period
              )?.label
            }
          </Text1>
        </StackItem>
        <StackItem label={t('予約受付締切')}>
          <Text1>
            {t(
              reservationDeadlineTimings[
                settings.reservation_deadline_timing || 'just_before'
              ]
            )}
          </Text1>
        </StackItem>
        <StackItem label={t('診療時間枠')}>
          <Text1>
            {settings.reservation_frame_period_minutes}
            {t('分')}
          </Text1>
        </StackItem>
      </div>
      <H3 className={classes.subHeader}>{t('定期予約')}</H3>
      <div className={classes.category}>
        <StackItem label={t('予約受付時間')}>
          <Text1>
            {settings.reservation_frame_start_time || '08:00'}〜
            {!settings.reservation_frame_end_time
              ? '20:00'
              : settings.reservation_frame_end_time}
          </Text1>
        </StackItem>
        <RegularReservationFrameSetting
          unit={settings.reservation_frame_period_minutes}
          from={settings.reservation_frame_start_time || '08:00'}
          to={settings.reservation_frame_end_time || '20:00'}
          reservationSettings={settings.regular_reservation_settings}
          readOnly
        />
      </div>
      <div className={classes.noWrapper}>
        <IndividualReservationSettingList
          unit={settings.reservation_frame_period_minutes}
          reservationSettings={settings.individual_reservation_settings}
          readOnly
        />
      </div>
      <div className={classes.noWrapper}>
        <CloseDateSettingList
          closeDateSettings={settings.close_date_settings}
          readOnly
        />
      </div>
      <H2>{t('ヒアリング項目')}</H2>
      <div className={classes.category}>
        <StackItem label={t('自由記述欄')}>
          <Text1>
            {SelectOptionValue(settings.free_description_field_setting)}
          </Text1>
        </StackItem>
        <StackItem label={t('自由記述欄補足文')} borderBottom alignTop>
          <Text1>
            <LinkUrlText>
              {settings?.free_description_supplement || ''}
            </LinkUrlText>
          </Text1>
        </StackItem>
        <StackItem label={t('ファイル添付')}>
          <Text1>{SelectOptionValue(settings.attachment_image_setting)}</Text1>
        </StackItem>
        <StackItem label={t('ファイル添付補足文')}>
          <Text1>{settings.attachment_image_supplement}</Text1>
        </StackItem>
      </div>
      <H2>{t('受診方法')}</H2>
      <div className={classes.category}>
        <StackItem label={t('対面受診の選択肢')}>
          <Text1>
            {settings.use_in_person_consultation
              ? t('使用する')
              : t('使用しない')}
          </Text1>
        </StackItem>
      </div>
      <H2>{callSettingHeaderContent}</H2>
      <div className={classes.category}>
        <CallMethodRead callMethods={settings.call_methods} />
        {!settings.call_methods.find((m) => m.is_active) && (
          <StackItem label={t('連絡事項')} alignTop>
            <Text1 className={classes.preWrap}>
              {settings?.reservation_message}
            </Text1>
          </StackItem>
        )}
      </div>
      <H2>{t('申込者')}</H2>
      <div className={classes.category}>
        <DepartmentStaticItems />
        <StackItem label={t('住所')}>
          <Text1>{SelectOptionValue(settings.applicant_address_setting)}</Text1>
        </StackItem>
        <StackItem label={t('診察券番号')}>
          <Text1>
            {SelectOptionValue(settings.registration_card_number_setting)}
          </Text1>
        </StackItem>
        <StackItem label={t('保険証・マイナ保険証')}>
          <Text1>{SelectOptionValue(settings.insurance_card_setting)}</Text1>
        </StackItem>

        <StackItem label={t('保険証以外の証書')}>
          <Text1>{SelectOptionValue(settings.certificate_setting)}</Text1>
        </StackItem>
        <StackItem label={t('本人確認書類')}>
          <Text1>
            {SelectOptionValue(settings.identification_document_setting)}
          </Text1>
        </StackItem>
      </div>

      <H2>{t('決済')}</H2>
      <div className={classes.category}>
        <StackItem
          label={
            <QuestionTooltipWithLabel label={t('決済方法')}>
              {t('申込者にメールでお知らせします。')}
            </QuestionTooltipWithLabel>
          }
        >
          <Text1>
            {settings.settlement_settings
              .filter((s) => s.is_active)
              .map((s) => t(SettlementsNames[s.name]))
              .join('、')}
          </Text1>
        </StackItem>
      </div>
    </div>
  )
}

export default DepartmentRead
